import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Group,
  NumberInput,
  Text,
  Radio,
  Stack,
  TextInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import Popover from "./PopoverField";
import PopoverField from "./PopoverField";
import DateInput from "./components/DateTimeInput";
import AddressInput from "./components/AddressInput";
import useLocations from "./hooks/useLocations";
import {
  truncate,
  startingFromString,
  endingAtString,
} from "./helpers/MiniGetAQuoteHelpers";
import { sendData } from "./helpers/Api";
import { Headset } from "./icons";
import { track } from "./analytics/analytics";

export default function QuoteField() {
  const [opened, setOpened] = useState(false);
  const [tripType, setTripType] = useState("one_way");
  const [numPassengers, setNumPassengers] = useState(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const {
    locations,
    setLocations,
    updateLocation,
    stopOrderIsValid,
    hasAllLocations,
    hasValidLocation,
  } = useLocations(tripType);

  const handleSendData = () => {
    const data = {
      tripType,
      numPassengers,
      locations,
      isMobile,
    };
    sendData(data, isMobile);
  };

  const isFormValid = () => {
    if (isMobile) {
      return locations[0].address;
    } else {
      return (
        stopOrderIsValid &&
        hasAllLocations &&
        hasValidLocation &&
        numPassengers &&
        numPassengers > 0
      );
    }
  };

  useEffect(() => {
    track("Trip type changed", { tripType });
  }, [tripType]);

  return (
    <>
      <Box p={20} style={{ backgroundColor: "#142621" }}>
        <Group justify={isMobile ? "center" : "flex-start"}>
          <Radio
            label="One-way"
            checked={tripType === "one_way"}
            onChange={() => setTripType("one_way")}
            styles={{
              label: {
                color: "#fff",
              },
            }}
          />
          <Radio
            label="Round Trip"
            checked={tripType === "round_trip"}
            onChange={() => setTripType("round_trip")}
            styles={{
              label: {
                color: "#fff",
              },
            }}
          />
        </Group>
      </Box>
      {isMobile && (
        <Box
          p={20}
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#1f312c",
          }}
        >
          <Stack gap="md" w="100%">
            <Text
              ta="center"
              style={{ fontSize: 20, fontWeight: 600, color: "#fff" }}
            >
              Where are you heading?
            </Text>
            <Box onClick={() => handleSendData()}>
              <AddressInput />
            </Box>
            <Button
              color="#74CBAF"
              variant="transparent"
              onClick={() => handleSendData()}
              style={{
                color: "#74CBAF",
                pointerEvents: "auto",
                "--button-disabled-bg": "transparent",
                "--button-disabled-color": "#666",
              }}
              styles={{
                root: {
                  opacity: 1,
                  border: "none",
                },
              }}
            >
              Tap to start planning your trip
            </Button>
          </Stack>
        </Box>
      )}
      {!isMobile && (
        <Box p={20} style={{ backgroundColor: "#1f312c" }}>
          <Flex gap="md" align="flex-end">
            <Box flex={3}>
              <PopoverField
                label="Starting from"
                value={startingFromString(locations)}
                width="target"
                required
              >
                <Stack gap="md">
                  <AddressInput
                    key={locations[0].id}
                    location={locations[0]}
                    index={0}
                    updateLocation={updateLocation}
                    locationType={locations[0].type}
                  />
                  <DateInput
                    locations={locations}
                    location={locations[0]}
                    index={0}
                    updateLocation={updateLocation}
                  />
                </Stack>
              </PopoverField>
            </Box>
            <Box flex={2}>
              <PopoverField
                label={tripType === "round_trip" ? "Going to" : "Ending at"}
                value={endingAtString(locations)}
                width="270px"
                required
              >
                <AddressInput
                  key={locations[1].id}
                  location={locations[1]}
                  index={1}
                  updateLocation={updateLocation}
                  locationType={locations[1].type}
                />
                {tripType === "round_trip" && (
                  <DateInput
                    locations={locations}
                    location={locations[1]}
                    index={1}
                    updateLocation={updateLocation}
                    isRoundTrip={tripType === "round_trip"}
                  />
                )}
              </PopoverField>
            </Box>
            <Box flex={1}>
              <NumberInput
                value={numPassengers}
                onChange={setNumPassengers}
                label={
                  <>
                    Passengers
                    <span style={{ color: "#ff6b6b" }}> *</span>
                  </>
                }
                allowNegative={false}
                min={1}
                hideControls
                styles={{
                  input: {
                    background: "transparent",
                    border: "none",
                    borderBottom: "1px solid white",
                    borderRadius: 0,
                    color: "white",
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginTop: 8,
                  },
                  label: {
                    color: "#fff",
                  },
                }}
              />
            </Box>
            <Button
              h={44}
              w={132}
              color="#D15A00"
              variant="filled"
              disabled={!isFormValid()}
              onClick={() => handleSendData()}
              style={{
                pointerEvents: isFormValid() ? "auto" : "none",
                backgroundColor: isFormValid() ? "#D15A00" : "#CCCCCC",
                color: isFormValid() ? "#ffffff" : "#888888",
                border: "none",
                boxShadow: "none",
                transition: "background-color 150ms ease",
              }}
            >
              See pricing
            </Button>
          </Flex>
        </Box>
      )}
      <Box
        style={{
          background: "#74CBAF",
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!isMobile && (
          <>
            <Text
              style={{
                paddingRight: 4,
                color: "#1F312C",
                fontSize: 16,
                fontWeight: 500,
              }}
            >
              Planning something complex, or don’t have all your details?
            </Text>
            <Headset style={{ color: "#1F312C" }} width={15} height={15} />
            <Button
              variant="transparent"
              style={{
                textDecoration: "underline",
                paddingLeft: 4,
                color: "#1F312C",
              }}
              onClick={() => {
                window.location.href = "/get-a-quote";
              }}
            >
              Work with a specialist
            </Button>
          </>
        )}
        {isMobile && (
          <Group gap={4} wrap="nowrap" justify="center">
            <Text
              size="12px"
              fw={500}
              style={{ color: "#1F312C", whiteSpace: "nowrap" }}
            >
              Don't have all your details yet?
            </Text>

            <Text
              component="a"
              href="/get-a-quote"
              size="12px"
              fw={500}
              style={{
                color: "#1F312C",
                textDecoration: "underline",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              Work with a specialist
            </Text>
          </Group>
        )}
      </Box>
    </>
  );
}
